import { Search } from "lucide-react"
/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in federated-search.css. */
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type RefObject,
} from "react"

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"
import { useSupportsIntersectionObserver } from "@/hooks/useSupportsIntersectionObserver"

import "./federated-search.css"

type FederatedMode = "federated" | "mirror" | "offline"
type AnimationState = { mode: FederatedMode; time: number }

const stepLabels = ["query", "fan-out", "local search", "thin slice", "merge", "one list"]
const modeOrder: FederatedMode[] = ["federated", "mirror", "offline"]
const stepDuration = 2.2
const animationDuration = stepLabels.length * stepDuration
const captions: Record<FederatedMode, string[]> = {
  federated: [
    "One query enters at the center. Nothing has left it yet.",
    "The query goes to the data, not the other way around.",
    "Each region searches only the repos that it homes.",
    "A scored slice crosses each boundary. The source does not.",
    "The merge layer interleaves the three slices by score.",
    "One ranked list comes from data that never moved.",
  ],
  mirror: [
    "One query enters at the center. Nothing has left it yet.",
    "The query goes to the data, not the other way around.",
    "Web has a mirror in Europe. Only its home region answers.",
    "A scored slice crosses each boundary. The source does not.",
    "The merge layer interleaves the three slices by score.",
    "Re-homing changes where a repo lives. It does not create duplicates.",
  ],
  offline: [
    "One query enters at the center. Nothing has left it yet.",
    "The query fans out. One region will not answer.",
    "EU-central does not return. Its progress bar does not fill.",
    "Two slices cross the boundary. The third does not arrive.",
    "The merge continues without the EU-central results.",
    "The response is flagged as incomplete. It is not silently partial.",
  ],
}
const regions = [
  { id: "us", label: "us-east", repos: ["web", "search"] },
  { id: "eu", label: "eu-central", repos: ["infra", "docs"] },
  { id: "ap", label: "ap-south", repos: ["cli"] },
] as const
const baseResults = [
  { repo: "search", region: "us-east", score: "0.94" },
  { repo: "infra", region: "eu-central", score: "0.81" },
  { repo: "cli", region: "ap-south", score: "0.69" },
  { repo: "web", region: "us-east", score: "0.58" },
] as const

function useIsVisible(targetRef: RefObject<HTMLElement | null>) {
  const supportsIntersectionObserver = useSupportsIntersectionObserver()
  const [isVisible, setIsVisible] = useState(false)
  useEffect(() => {
    const target = targetRef.current
    if (!target || !supportsIntersectionObserver) return
    const observer = new IntersectionObserver(
      (entries) => setIsVisible(entries.some((entry) => entry.isIntersecting)),
      { rootMargin: "160px 0px" },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [supportsIntersectionObserver, targetRef])
  return isVisible || !supportsIntersectionObserver
}

function progressBetween(time: number, start: number, end: number) {
  return Math.min(1, Math.max(0, (time - start) / (end - start)))
}

function getStep(time: number) {
  if (time >= animationDuration) return stepLabels.length - 1
  return Math.min(Math.floor(time / stepDuration), stepLabels.length - 1)
}

function getTimelineProgress(time: number) {
  return stepLabels.map((_, index) =>
    progressBetween(time, index * stepDuration, (index + 1) * stepDuration),
  )
}

function getNextMode(mode: FederatedMode) {
  const currentIndex = modeOrder.indexOf(mode)
  return modeOrder[(currentIndex + 1) % modeOrder.length]!
}

function modeStepLabel(mode: FederatedMode, step: number) {
  if (mode === "mirror" && step === 2) return "re-homed"
  if (mode === "mirror" && step === 5) return "one home"
  if (mode === "offline" && step === 2) return "no answer"
  if (mode === "offline" && step === 5) return "flagged"
  return stepLabels[step]
}

export function FederatedSearch() {
  const rootRef = useRef<HTMLElement>(null)
  const animationFrameRef = useRef<number | null>(null)
  const previousFrameRef = useRef<number | null>(null)
  const reducedMotion = usePrefersReducedMotion()
  const isVisible = useIsVisible(rootRef)
  const [{ mode, time }, setAnimationState] = useState<AnimationState>({
    mode: "federated",
    time: 0,
  })
  const [isPlaying, setIsPlaying] = useState(!reducedMotion)
  const displayTime = reducedMotion ? animationDuration : time
  const step = getStep(displayTime)
  const timelineProgress = getTimelineProgress(displayTime)

  useEffect(() => {
    if (!isPlaying || !isVisible || reducedMotion) {
      previousFrameRef.current = null
      return
    }

    const animate = (timestamp: number) => {
      const previousFrame = previousFrameRef.current ?? timestamp
      const delta = Math.min(0.06, (timestamp - previousFrame) / 1000)
      previousFrameRef.current = timestamp
      setAnimationState((currentState) => {
        const nextTime = currentState.time + delta
        if (nextTime < animationDuration) return { ...currentState, time: nextTime }
        return { mode: getNextMode(currentState.mode), time: 0 }
      })
      animationFrameRef.current = window.requestAnimationFrame(animate)
    }

    animationFrameRef.current = window.requestAnimationFrame(animate)
    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current)
      }
      animationFrameRef.current = null
      previousFrameRef.current = null
    }
  }, [isPlaying, isVisible, reducedMotion])

  const selectMode = (nextMode: FederatedMode) => {
    setAnimationState({ mode: nextMode, time: 0 })
    setIsPlaying(!reducedMotion)
  }

  const changeTime = (event: ChangeEvent<HTMLInputElement>) => {
    const nextTime = Number(event.currentTarget.value)
    setAnimationState((currentState) => ({ ...currentState, time: nextTime }))
  }
  const results =
    mode === "offline"
      ? baseResults.filter((result) => result.region !== "eu-central")
      : baseResults

  return (
    <figure className="federated-search not-prose" data-mode={mode} data-step={step} ref={rootRef}>
      <div className="federated-search__modes" aria-label="Search scenario" role="group">
        {modeOrder.map((option) => (
          <button
            aria-pressed={mode === option}
            key={option}
            onClick={() => selectMode(option)}
            type="button"
          >
            {option === "mirror" ? (
              "mirror web"
            ) : option === "offline" ? (
              <>
                <span className="federated-search__mode-label--full">EU-central offline</span>
                <span aria-hidden="true" className="federated-search__mode-label--compact">
                  EU-C. OFFLINE
                </span>
              </>
            ) : (
              option
            )}
          </button>
        ))}
      </div>

      <div className="federated-search__stage">
        <div className="federated-search__query">
          <Search aria-hidden="true" className="federated-search__query-icon" />
          <span>rate limit retry logic</span>
        </div>
        <svg
          className="federated-search__wires"
          viewBox="0 0 800 255"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M400 34 V70 H132 V105" />
          <path d="M400 34 V105" />
          <path d="M400 70 H668 V105" />
        </svg>

        <div className="federated-search__regions">
          {regions.map((region) => {
            const isOffline = mode === "offline" && region.id === "eu"
            return (
              <div
                className={`federated-search__region federated-search__region--${region.id}`}
                key={region.id}
              >
                <div className="federated-search__region-header">
                  <span>
                    <i />
                    {region.label}
                  </span>
                  <small>{isOffline ? "unreachable" : "data stays here"}</small>
                </div>
                <div className="federated-search__repo-list">
                  {region.repos.map((repo) => (
                    <span key={repo}>repo: {repo}</span>
                  ))}
                  {mode === "mirror" && region.id === "eu" ? (
                    <span className="federated-search__mirror">
                      web · mirror <small>not searched</small>
                    </span>
                  ) : null}
                </div>
                <div className="federated-search__progress">
                  <span />
                </div>
                <div className="federated-search__slice">ranked slice</div>
              </div>
            )
          })}
        </div>

        <div className="federated-search__merge">
          <div className="federated-search__merge-header">
            <strong>One ranked list</strong>
            <span>
              {results.length} results ·{" "}
              {mode === "offline" ? "2/3 regions · incomplete" : "3 regions"}
            </span>
          </div>
          <div className="federated-search__results">
            {results.map((result, index) => (
              <div
                className="federated-search__result"
                key={result.repo}
                style={{ "--result-delay": `${index * 80}ms` } as CSSProperties}
              >
                <strong>{result.repo}</strong>
                <span>
                  {result.region} · {result.score}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="federated-search__reader">
        <div className="federated-search__timeline">
          <div className="federated-search__timeline-segments" aria-hidden="true">
            {stepLabels.map((label, index) => (
              <span key={label}>
                <span style={{ width: `${(timelineProgress[index] ?? 0) * 100}%` }} />
              </span>
            ))}
          </div>
          <input
            aria-label="Animation progress"
            max={animationDuration}
            min={0}
            onChange={changeTime}
            step={0.01}
            type="range"
            value={displayTime}
            disabled={reducedMotion}
          />
          <button
            aria-label={isPlaying ? "Pause animation" : "Play animation"}
            disabled={reducedMotion}
            onClick={() => setIsPlaying((value) => !value)}
            type="button"
          >
            {reducedMotion ? "Reduced motion" : isPlaying ? "Pause" : "Play"}
          </button>
        </div>
        <figcaption className="federated-search__caption" aria-live="polite">
          <strong>
            {String(step + 1).padStart(2, "0")} / {modeStepLabel(mode, step)}
          </strong>
          <span>{captions[mode][step]}</span>
        </figcaption>
      </div>
    </figure>
  )
}
